import {AddressFormValue} from "../../data/models/subscription-order.model";
import {isAddressFormValueEmpty} from "./external-address-merge";

/** Die im Bestell-State vorhandenen Adressen, aus denen sich das Prämien-Ziel ableitet. */
export type OrderAddresses = {
  deliveryAddress?: AddressFormValue;
  billingAddress?: AddressFormValue;
};

/**
 * Die Adresse, an die die Prämie geliefert wird, wenn der Nutzer keine abweichende angibt:
 * die Lieferadresse, ersatzweise die Rechnungsadresse.
 *
 * Liefert undefined, wenn noch gar keine Adresse erfasst wurde - dann muss der Bonus-Step die
 * Prämienadress-Maske sofort aufklappen, statt eine Kachel mit dem Lieferziel zu zeigen.
 */
export function resolveBonusAddressTarget(addresses: OrderAddresses): AddressFormValue | undefined {
  if (!isAddressFormValueEmpty(addresses?.deliveryAddress)) {
    return addresses.deliveryAddress;
  }
  if (!isAddressFormValueEmpty(addresses?.billingAddress)) {
    return addresses.billingAddress;
  }
  return undefined;
}

/**
 * Die Prämienadresse, die an das Bestell-Objekt gehängt wird - in beiden Fällen eine:
 *
 * - Hat der Nutzer "Abweichende Prämienadresse" ausgefüllt, gilt diese.
 * - Sonst gilt das Lieferziel aus resolveBonusAddressTarget (Liefer-, ersatzweise Rechnungsadresse).
 *
 * Bei zugeklappter Checkbox sind die Inhalts-Gruppen der bonusAddress deaktiviert (P5W-4462), im
 * Formularwert steht dann keine Adresse - deshalb der Rückgriff auf das Lieferziel.
 */
export function resolveBonusAddress(
  enteredBonusAddress: AddressFormValue,
  addresses: OrderAddresses
): AddressFormValue | undefined {
  if (!isAddressFormValueEmpty(enteredBonusAddress)) {
    return enteredBonusAddress;
  }
  return resolveBonusAddressTarget(addresses);
}

/**
 * Die Anschrift als fertige Anzeige-Zeilen (Anrede + Name / Straße + Hausnummer / PLZ + Ort).
 *
 * Arbeitet direkt auf dem Formularwert und toleriert dessen beide Schreibweisen (firstName und
 * firstname, houseNo und houseno), weil Formular, PayPal-Mapping und Vorbefüllung sie gemischt
 * liefern. Die Anrede kommt je nach Quelle als Objekt mit 'description' oder als blanker Schlüssel
 * ('1', 'FR'); für den Schlüssel übergibt der Aufrufer mit salutationText die Auflösung aus den
 * Stammdaten, sonst steht der Schlüssel selbst da. Leere Zeilen fallen weg; eine Adresse ohne jede
 * Angabe ergibt eine leere Liste.
 */
export function formatAddressLines(
  address: AddressFormValue,
  salutationText?: (key: string) => string
): string[] {
  if (!address) {
    return [];
  }

  const person: any = address.person ?? {};
  const anschrift: any = address.address ?? {};

  const text = (value: unknown): string => (value ?? '').toString().trim();
  const salutationKey = person.salutation !== null && typeof person.salutation === 'object'
    ? text(person.salutation.description ?? person.salutation.key)
    : text(person.salutation);
  const salutation = salutationKey === '' ? '' : (text(salutationText?.(salutationKey)) || salutationKey);

  return [
    [salutation, text(person.title), text(person.firstName ?? person.firstname), text(person.lastName ?? person.lastname)],
    [text(anschrift.street), text(anschrift.houseNo ?? anschrift.houseno)],
    [text(anschrift.zipcode), text(anschrift.city)]
  ]
    .map(parts => parts.filter(part => part !== '').join(' '))
    .filter(line => line !== '');
}

/**
 * Hat der Nutzer überhaupt schon eine Adresse erfasst - Liefer- oder Rechnungsadresse?
 *
 * Maßgeblich ist der Inhalt, nicht das Vorhandensein des Objekts: eine vorbefüllte, aber leere
 * Adresse (Bestandskunde ohne Stammadresse, zugeklappter Block) zählt nicht als Adresse.
 */
export function hasOrderAddress(addresses: OrderAddresses): boolean {
  return !!resolveBonusAddressTarget(addresses);
}
